/**
 * Project URLs (registry projects.create-project AC2, AC13): `/<ws>/project/<ascii-slug>-<slugId>/<tab>`. The slug id
 * is the last 12 hex digits of the project's client-minted UUIDv7 (its random tail), so the client knows the URL
 * before the server answers. The name part is ASCII only and decorative: a project is found by its slug id, so a
 * rename never breaks a link and a name with no ASCII letters falls back to the bare slug id.
 */

/** `slug_id` of a project id (the database CHECK is `^[0-9a-f]{12}$`). */
export function slugIdOf(projectId: string): string {
  return projectId.replace(/-/g, '').slice(-12).toLowerCase();
}

/** The ASCII, lowercase, dash-separated form of a name ('' when nothing ASCII is left). */
export function asciiSlug(name: string, max = 60): string {
  const base = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base.slice(0, max).replace(/-+$/g, '');
}

/** The `$slug` route param of a project. */
export function projectSlug(name: string, slugId: string): string {
  const s = asciiSlug(name);
  return s ? `${s}-${slugId}` : slugId;
}

/** The slug id inside a route param ('payments-dashboard-0a1b2c3d4e5f' → '0a1b2c3d4e5f'), or null. */
export function slugIdFromParam(param: string): string | null {
  const m = /(?:^|-)([0-9a-f]{12})$/.exec(param.toLowerCase());
  return m ? m[1]! : null;
}

export const PROJECT_TABS = ['overview', 'updates', 'issues'] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];

export function isProjectTab(v: unknown): v is ProjectTab {
  return typeof v === 'string' && (PROJECT_TABS as readonly string[]).includes(v);
}

/** Workspace-relative path of a project tab ('/project/payments-dashboard-0a1b…/overview'). */
export function projectPath(name: string, slugId: string, tab: string = 'overview'): `/${string}` {
  return `/project/${projectSlug(name, slugId)}/${tab}`;
}
